import React from 'react';
import { Switch } from '@patternfly/react-core';

export interface BaseSwitchProps {
    isChecked: boolean;
    onChange: (checked: boolean) => void;
    label?: string;
    id?: string;
    ouiaId?: string;
    isDisabled?: boolean;
}

export const BaseSwitch: React.FC<BaseSwitchProps> = ({
    isChecked,
    onChange,
    label,
    id,
    ouiaId,
    isDisabled = false
}) => {
    const handleChange = (_event: React.FormEvent<HTMLInputElement>, checked: boolean) => {
        onChange(checked);
    };

    return (
        <Switch
            id={id}
            label={label}
            isChecked={isChecked}
            onChange={handleChange}
            ouiaId={ouiaId}
            isDisabled={isDisabled}
        />
    );
};

export interface UseSwitchOptions {
    defaultValue?: boolean;
}

export interface UseSwitchReturn {
    value: boolean;
    handleChange: (checked: boolean) => void;
    setValue: (value: boolean) => void;
}

export const useSwitch = (options: UseSwitchOptions = {}): UseSwitchReturn => {
    const { defaultValue = false } = options;
    const [value, setValue] = React.useState<boolean>(defaultValue);

    const handleChange = (checked: boolean) => {
        setValue(checked);
    };

    return {
        value,
        handleChange,
        setValue
    };
};

// Generic interface for specific switch components
export interface BaseSwitchComponentProps extends BaseSwitchProps {
    // Support for any additional props
    [key: string]: any;
}

// Factory function to create switch components with less boilerplate
export const createSwitchComponent = (
    name: string,
    config: {
        defaultLabel?: string;
        defaultId?: string;
        defaultOuiaId?: string;
        getLabel?: (props: any) => string;
        getId?: (props: any) => string;
        getOuiaId?: (props: any) => string;
    } = {}
) => {
    const SwitchComponent: React.FC<BaseSwitchComponentProps> = ({
        label,
        id,
        ouiaId,
        ...props
    }) => {
        // Determine label dynamically if getLabel is provided
        const finalLabel = label || 
            (config.getLabel ? config.getLabel(props) : config.defaultLabel);
        
        // Determine id dynamically if getId is provided
        const finalId = id || 
            (config.getId ? config.getId(props) : config.defaultId);

        // Determine ouiaId dynamically if getOuiaId is provided
        const finalOuiaId = ouiaId || 
            (config.getOuiaId ? config.getOuiaId(props) : config.defaultOuiaId);

        return (
            <BaseSwitch
                {...props}
                label={finalLabel}
                id={finalId}
                ouiaId={finalOuiaId}
            />
        );
    };

    SwitchComponent.displayName = name;
    return SwitchComponent;
};
